import {
  Button,
  DismissIcon,
  Flex,
  JaneLogo,
  TextField,
  Typography,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Form } from 'react-router';

import { GoogleSignInButton } from '@jane/bloom/components/googleSignIn/googleSignIn';
import { useCheckJaneUserExists } from '@jane/bloom/hooks/account/useCheckJaneUserExists';
import { useFieldErrorVisibility } from '@jane/bloom/hooks/forms/useFieldErrorVisibility';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { CheckoutAuthFormStep } from '@jane/bloom/routes/_menu.$basename.checkout._index/types';
import { stricterEmailRegex } from '@jane/bloom/utils/strings/stricterEmailRegex';

import styles from './loginModal.module.css';

interface SignInProps {
  onClose: () => void;
  setFormStep: (formStep: CheckoutAuthFormStep) => void;
  setUserEmail: (email: string) => void;
  skipBagAfterAuth: boolean;
  userEmail: string;
}

export const SignIn = ({
  onClose,
  setFormStep,
  setUserEmail,
  skipBagAfterAuth,
  userEmail,
}: SignInProps) => {
  const { googleSsoClientId } = useRootLoaderData();
  const { checkUserExists, fetcher } = useCheckJaneUserExists();
  const [formSubmitted, setFormSubmitted] = useState(false);

  const submit = ({ email }: { email: string }) => {
    setFormSubmitted(true);
    checkUserExists(email);
    setUserEmail(email);
  };

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm({
    defaultValues: {
      email: userEmail ?? '',
    },
    mode: 'all',
  });

  const { getShowError, setShowError } = useFieldErrorVisibility([
    { id: 'email' },
  ]);

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data?.success && formSubmitted) {
      const { isNewUser } = fetcher.data.payload;
      if (isNewUser) {
        setFormStep('createAccount');
      } else {
        setFormStep('existingUser');
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher.data, fetcher.state, formSubmitted]);

  return (
    <Flex flexDirection="column" alignItems="center">
      <Button.Icon
        className={styles['closeButton']}
        icon={<DismissIcon />}
        label="close"
        onClick={onClose}
      />
      <Flex flexDirection="column" alignItems="center" px={24} pb={24}>
        <Typography
          variant="header"
          textAlign="center"
          className={styles['header']}
        >
          {"Let's check if you have an account"}
        </Typography>
        {googleSsoClientId && (
          <>
            <GoogleSignInButton
              googleSsoClientId={googleSsoClientId}
              skipBagAfterAuth={skipBagAfterAuth}
            />
            <Typography mt={16} mb={16} textAlign="center">
              or
            </Typography>
          </>
        )}
        <Form
          name="Account Sign In Form"
          className={styles['signInForm']}
          onSubmit={handleSubmit(submit)}
        >
          <Controller
            name="email"
            control={control}
            rules={{
              pattern: {
                message: 'Invalid email. Please try again.',
                value: stricterEmailRegex,
              },
              required: 'Invalid email. Please try again.',
            }}
            render={({ field }) => (
              <TextField
                disableMobileInputStyling
                type="email"
                label="Email"
                /**
                 * @todo unpack a possible naming clash between Controller and field.
                 * With the same input name, the field does not register the incoming
                 * email from 1password autocomplete on Safari.
                 */
                name="_email"
                width="100%"
                placeholder="Enter your email"
                defaultValue={userEmail}
                className={clsx({
                  [styles['errorPassTextField']]:
                    getShowError('email') && errors.email,
                })}
                onChange={(email) => {
                  field.onChange(email);
                }}
                onBlur={() => {
                  setShowError('email', true);
                }}
              />
            )}
          />
          {errors.email && getShowError('email') && (
            <Typography variant="mini" color="error" mt={8}>
              {errors.email.message}
            </Typography>
          )}
          <Button
            label="Continue"
            full
            my={24}
            loading={fetcher.state !== 'idle'}
            type="submit"
            disabled={!isValid}
            variant="primary"
          />
        </Form>
        <Typography
          as="span"
          variant="mini"
          mx={32}
          textAlign="center"
          color="grays-mid"
          className={styles['lineHeight']}
        >
          Sign in to check your reservations, status updates, and personal
          details—securely backed by{' '}
          <JaneLogo
            size="xs"
            variant="secondary"
            className={styles['janeLogo']}
          />
        </Typography>
      </Flex>
    </Flex>
  );
};
